import { useState, useEffect, useRef, useMemo } from "react";
import { Loader2, Phone, RefreshCw, RotateCcw } from "lucide-react";
import type { VoiceProviderSettings } from "@type/agent";
import { AVAILABLE_VOICES } from "@type/agent";
import {
    getVoiceProviderSettings,
    updateVoiceProvider,
    syncVoiceProvider,
    getAgentPreferences,
    updateAgentPreferences,
} from "@service/agent";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Button } from "@component/button";
import { Badge } from "@component/badge";
import SettingsTwoPanelLayout from "@component/setting-two-panel";
import { cn } from "@util/tailwind";

export default function AgentSettingsContainer() {
    const snackbar = useSnackbar();

    // Voice provider state (existing)
    const [voiceProvider, setVoiceProvider] = useState<VoiceProviderSettings | null>(null);
    const initialVoiceProvider = useRef<VoiceProviderSettings | null>(null);

    // Agent preferences state
    const [selectedVoice, setSelectedVoice] = useState("en-west-f");
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    const initialPrefs = useRef({ voiceId: "en-west-f", language: "en-west" });

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [isSavingPrefs, setIsSavingPrefs] = useState(false);
    const [isSavingProvider, setIsSavingProvider] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<{ connected: boolean; agentCount?: number } | null>(null);

    const loadSettings = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const [vp, prefs] = await Promise.all([
                getVoiceProviderSettings(),
                getAgentPreferences().catch(() => ({ voiceId: "en-west-f", language: "en-west" })),
            ]);
            setVoiceProvider(vp);
            initialVoiceProvider.current = vp;
            setSelectedVoice(prefs.voiceId);
            setSelectedLanguage(prefs.language);
            initialPrefs.current = prefs;
        } catch (err) {
            setLoadError(true);
            catchErrorLog(`Failed to load agent settings: ${err}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const hasPrefsChanges = useMemo(() => {
        return (
            selectedVoice !== initialPrefs.current.voiceId ||
            selectedLanguage !== initialPrefs.current.language
        );
    }, [selectedVoice, selectedLanguage]);

    const hasProviderChanges = useMemo(() => {
        if (!voiceProvider || !initialVoiceProvider.current) return false;
        return (
            voiceProvider.provider !== initialVoiceProvider.current.provider ||
            voiceProvider.phoneNumberId !== initialVoiceProvider.current.phoneNumberId
        );
    }, [voiceProvider]);

    const isProviderValid = useMemo(() => {
        if (!voiceProvider) return false;
        return voiceProvider.provider.trim().length > 0 && voiceProvider.phoneNumberId.trim().length > 0;
    }, [voiceProvider]);

    const handleSavePrefs = async () => {
        setIsSavingPrefs(true);
        try {
            await updateAgentPreferences({ voiceId: selectedVoice, language: selectedLanguage });
            initialPrefs.current = { voiceId: selectedVoice, language: selectedLanguage };
            snackbar.showSuccess("Voice and language preferences saved.");
        } catch (err) {
            snackbar.showError("Unable to save preferences.", "Please try again.");
            catchErrorLog(`Failed to save agent preferences: ${err}`);
        } finally {
            setIsSavingPrefs(false);
        }
    };

    const handleSaveProvider = async () => {
        if (!voiceProvider) return;
        setIsSavingProvider(true);
        try {
            await updateVoiceProvider({
                phoneNumberId: voiceProvider.phoneNumberId,
                provider: voiceProvider.provider,
                config: voiceProvider.config,
            });
            initialVoiceProvider.current = { ...voiceProvider };
            snackbar.showSuccess("Voice provider settings saved successfully.");
        } catch (err) {
            snackbar.showError("Unable to save voice provider settings.", "Please check your connection and try again.");
            catchErrorLog(`Failed to save agent settings: ${err}`);
        } finally {
            setIsSavingProvider(false);
        }
    };

    const handleSync = async () => {
        setIsSyncing(true);
        try {
            const result = await syncVoiceProvider();
            setSyncResult(result);
            snackbar.showSuccess(
                result.connected
                    ? `Voice provider connected${result.agentCount !== undefined ? ` · ${result.agentCount} agents` : ""}`
                    : "Voice provider is disconnected. Check your credentials."
            );
        } catch (err) {
            snackbar.showError("Unable to sync voice provider.", "Please check your connection and try again.");
            catchErrorLog(`Failed to sync voice provider: ${err}`);
        } finally {
            setIsSyncing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (loadError || !voiceProvider) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <p className="text-sm text-gray-500">Failed to load settings.</p>
                <Button variant="outline" onClick={loadSettings}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Retry
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-black">Settings</h1>
                <p className="text-sm text-gray-600 mt-1">Configure your agent's voice, language, and provider settings.</p>
            </div>

            <div className="space-y-8">
                {/* Default Voice Selection */}
                <SettingsTwoPanelLayout
                    title="Default Voice"
                    description="Set the default voice for new campaigns. Each campaign can override this in its Configuration tab."
                    showSaveButton={true}
                    onSave={handleSavePrefs}
                    isLoading={isSavingPrefs}
                    hasChanges={hasPrefsChanges}
                >
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                        {AVAILABLE_VOICES.map((voice) => (
                            <button
                                key={voice.id}
                                type="button"
                                onClick={() => setSelectedVoice(voice.id)}
                                className={cn(
                                    "rounded-lg border-2 p-4 text-left transition-all",
                                    selectedVoice === voice.id
                                        ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 dark:border-blue-400"
                                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                                )}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{voice.name}</p>
                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 leading-4">
                                        {voice.gender === "female" ? "F" : "M"}
                                    </Badge>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{voice.accent}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{voice.description}</p>
                            </button>
                        ))}
                    </div>
                </SettingsTwoPanelLayout>

                <hr className="border-gray-200" />

                {/* Voice Provider Connection */}
                <SettingsTwoPanelLayout
                    title="Connection"
                    description="Check your voice provider connection status and sync agents."
                >
                    <div className="flex items-start gap-6">
                        <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Phone className="w-7 h-7 text-blue-600" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-4">
                                <p className="text-sm font-medium text-gray-900 capitalize">
                                    Provider: {voiceProvider.provider || "elevenlabs"}
                                </p>
                                {syncResult && (
                                    <span
                                        className={`text-xs px-2 py-0.5 rounded-full ${
                                            syncResult.connected
                                                ? "bg-green-50 text-green-700"
                                                : "bg-red-50 text-red-700"
                                        }`}
                                    >
                                        {syncResult.connected ? "Connected" : "Disconnected"}
                                        {syncResult.agentCount !== undefined && ` · ${syncResult.agentCount} agents`}
                                    </span>
                                )}
                            </div>
                            <Button variant="secondary" size="sm" onClick={handleSync} disabled={isSyncing}>
                                <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? "animate-spin" : ""}`} />
                                {isSyncing ? "Syncing..." : "Sync Now"}
                            </Button>
                        </div>
                    </div>
                </SettingsTwoPanelLayout>

                <hr className="border-gray-200" />

                {/* Provider Configuration */}
                <SettingsTwoPanelLayout
                    title="Configuration"
                    description="Configure your voice provider credentials."
                    showSaveButton={true}
                    onSave={handleSaveProvider}
                    isLoading={isSavingProvider}
                    hasChanges={hasProviderChanges && isProviderValid}
                >
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="provider" style={{ color: "#000000" }}>
                                Provider
                            </Label>
                            <Input
                                id="provider"
                                value={voiceProvider.provider}
                                onChange={(e) => setVoiceProvider({ ...voiceProvider, provider: e.target.value })}
                                placeholder="elevenlabs"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="phoneNumberId" style={{ color: "#000000" }}>
                                Phone Number ID
                            </Label>
                            <Input
                                id="phoneNumberId"
                                value={voiceProvider.phoneNumberId}
                                onChange={(e) => setVoiceProvider({ ...voiceProvider, phoneNumberId: e.target.value })}
                                placeholder="Enter phone number ID"
                            />
                            <p className="text-xs text-gray-500">The phone number ID from your voice provider.</p>
                        </div>
                    </div>
                </SettingsTwoPanelLayout>
            </div>
        </div>
    );
}
